/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  snack,
  ApButtonDanger,
  ApButtonIcon,
  ApButtonSecondary,
  ApIcon,
  ApLoaderDefault,
  ApModal,
} from '@alixpartners/ui-components';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Col, Container, Row, Dotdotdot } from '../WrappedImports';

import { IReport } from '../../../../types';
import { Subject as SubjectIcon } from './Subject';
import InputModal from '../InputModal';

import './ReportBlock.scss';

interface ISharedReportBlockProps {
  report: IReport;
  innerRef?: any;
  provided?: any;
  isUnshareEnabled: boolean;
  isPowerBiEnabled: boolean;
  onDisplayNameSave: (id: string, navigationName: string) => void;
  onDescriptionSave: (id: string, description: string) => void;
  handleReportUnshare: (id: string) => Promise<any>;
  onReportUnshareDone: (report: IReport) => void;
}

const containerStyles = { width: '100%' };

const SharedReportBlock: React.SFC<ISharedReportBlockProps> = ({
  report,
  innerRef,
  provided,
  isUnshareEnabled,
  isPowerBiEnabled,
  onDisplayNameSave,
  onDescriptionSave,
  handleReportUnshare,
  onReportUnshareDone,
}) => {
  const [displayName, setDisplayName] = useState<string>(
    report.displayName || report.reportName
  );
  const [tempDisplayName, setTempDisplayName] = useState<string>(
    report.displayName || report.reportName
  );
  const [isDisplayNameModalOpen, setIsDisplayNameModalOpen] =
    useState<boolean>(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false);
  const [description, setDescription] = useState<string>(report.description);
  const [tempDescription, setTempDescription] = useState<string>(
    report.description
  );
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [error, setError] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [descriptionHeight, setDescriptionHeight] = useState<number>(0);

  const blockRef = useRef<HTMLDivElement>(null);
  const narrativeStyles = {
    height: `${descriptionHeight}px`,
  };

  useEffect(() => {
    if (blockRef.current) {
      setDescriptionHeight(blockRef.current.offsetHeight);
    }
  }, [isCollapsed]);

  const componentIsMounted = useRef(true);
  useEffect(() => {
    return () => {
      componentIsMounted.current = false;
    };
  }, []);

  const onRemoveModalClose = () => {
    setIsRemoveModalOpen(false);
  };

  return (
    <div className="report-block" ref={innerRef} {...provided.draggableProps}>
      <Container fluid style={containerStyles}>
        <Row justify="around">
          <Col xs={1}>
            <div
              {...provided.dragHandleProps}
              className="report-block__icon-container"
            >
              <ApIcon iconName="baseline_drag_handle" iconSize={24} />
            </div>
          </Col>
          <Col xs={2}>
            <div
              className={`report-block__collapsible${
                !isCollapsed ? ' report-block__collapsible--expanded' : ''
              }`}
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
              ref={blockRef}
            >
              <div className="report-block__collapsible__report-name">
                <Link
                  className="link"
                  to={`/client-report-site/${report.id}`}
                  target="_blank"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {report.reportName}
                </Link>
                <div>
                  <ApIcon iconName="baseline_arrow_drop_down" />
                </div>
              </div>
              {!isCollapsed && (
                <div className="report-block__collapsible__report-pages">
                  {report.reportPages.map((page) => (
                    <Link
                      key={page.id}
                      className="link-small"
                      to={`/client-report-site/${report.id}/${page.id}`}
                      target="_blank"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {page.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </Col>
          <Col xs={2}>
            <div
              className="report-block__display-name small-link"
              onClick={() => {
                setIsDisplayNameModalOpen(true);
              }}
            >
              {displayName}
            </div>
          </Col>
          <Col xs={3}>
            <div
              className={`report-block__description${
                !description ? ' report-block__description--no-description' : ''
              }`}
              style={narrativeStyles}
            >
              <SubjectIcon />
              {description ? (
                <div
                  className="report-block__description__text"
                  onClick={() => {
                    setIsDescriptionModalOpen(true);
                  }}
                >
                  <Dotdotdot
                    key={`description-${
                      isCollapsed ? 'collapsed' : 'expaneded'
                    }`}
                    clamp={
                      isCollapsed
                        ? 1
                        : descriptionHeight / report.reportPages.length
                    }
                  >
                    {description}
                  </Dotdotdot>
                </div>
              ) : (
                <div
                  className="small-link"
                  onClick={() => {
                    setIsDescriptionModalOpen(true);
                  }}
                >
                  Add a description
                </div>
              )}
            </div>
          </Col>
          <Col xs={3}>
            <div className="report-block__date-container-shared">
              {moment(report.sharedDate).format('D MMM YYYY, H:mm a')}
            </div>
          </Col>
          <Col xs={1}>
            {isUnshareEnabled && (
              <div className="report-block__remove-shared-container">
                {isLoading ? (
                  <ApLoaderDefault loaderSize="sm" />
                ) : (
                  <ApButtonIcon
                    iconName="baseline_close"
                    onClick={() => {
                      setIsRemoveModalOpen(true);
                    }}
                  />
                )}
              </div>
            )}
          </Col>
        </Row>
        {/* {error && (
          <Row justify="end">
            <Col>
              <div className="report-block__error">{error}</div>
            </Col>
          </Row>
        )} */}
      </Container>
      <InputModal
        id="display-name-modal"
        isOpen={isDisplayNameModalOpen}
        inputValue={tempDisplayName || ''}
        initialInputValue={displayName || ''}
        label="navigation name"
        maxLength={30}
        onModalClose={() => {
          setIsDisplayNameModalOpen(false);
          setTempDisplayName(displayName);
        }}
        onInputValueChange={(e) => {
          const { value } = e.target as HTMLInputElement;
          if (value.length > 30) {
            return;
          }
          setTempDisplayName(value);
        }}
        allowEmpty
        onSave={async () => {
          const actualDisplayName: string =
            tempDisplayName || report.reportName;
          if (!tempDisplayName) {
            setTempDisplayName(report.reportName);
          }
          try {
            await onDisplayNameSave(report.id, actualDisplayName);
            setDisplayName(actualDisplayName);
            setIsDisplayNameModalOpen(false);
          } catch (error) {
            snack.error({
              message: 'Sorry, there was an error while updating report',
            });
            setIsDisplayNameModalOpen(false);
            setTempDisplayName(displayName);
          }
          // onDisplayNameSave(report.id, actualDisplayName);
          // setDisplayName(actualDisplayName);
          // setIsDisplayNameModalOpen(false);
        }}
      />
      <InputModal
        id="description-modal"
        isOpen={isDescriptionModalOpen}
        inputValue={tempDescription || ''}
        initialInputValue={description || ''}
        allowEmpty
        label="narrative"
        type="textarea"
        maxLength={240}
        onModalClose={() => {
          setIsDescriptionModalOpen(false);
          setTempDescription(description);
        }}
        onInputValueChange={(e) => {
          const { value } = e.target as HTMLInputElement;
          if (value.length > 240) {
            return;
          }
          setTempDescription(value);
        }}
        onSave={async () => {
          try {
            await onDescriptionSave(report.id, tempDescription);
            setDescription(tempDescription);
            setIsDescriptionModalOpen(false);
          } catch (error) {
            snack.error({
              message: 'Sorry, there was an error while updating report',
            });
            setIsDescriptionModalOpen(false);
            setDescription(description);
          }
        }}
      />
      <ApModal
        className="remove-modal"
        isDismissible
        isOpen={isRemoveModalOpen}
        hasClosed={onRemoveModalClose}
        footer={() => (
          <div className="input-modal__footer">
            <ApButtonSecondary onClick={onRemoveModalClose}>
              Cancel
            </ApButtonSecondary>
            <ApButtonDanger
              onClick={async () => {
                // setError('');
                setIsLoading(true);
                setIsRemoveModalOpen(false);
                try {
                  await handleReportUnshare(report.id);
                  onReportUnshareDone(report);
                  if (componentIsMounted.current) {
                    setIsLoading(false);
                  }
                } catch (error) {
                  if (componentIsMounted.current) {
                    // setError('Sorry, there was an error while processing your request');
                    snack.error({
                      message:
                        'Sorry, there was an error while processing your request',
                    });
                    setIsLoading(false);
                  }
                }
              }}
            >
              Remove
            </ApButtonDanger>
          </div>
        )}
      >
        <div className="input-modal__content">
          {isPowerBiEnabled ? (
            <p>Are you sure you want to remove this report?</p>
          ) : (
            <p>
              Removing from the Report Site will delete this workbook from the
              Tableau Server. Consultant will need to re-publish using Tableau
              Desktop.
            </p>
          )}
        </div>
      </ApModal>
    </div>
  );
};

export default SharedReportBlock;
